import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../LanguageContext";
import { SERVICES_SEO } from "../seo/services";
import { STATES } from "../seo/states";

const LINK_IDS = ["services", "process", "websites", "pricing", "about", "faq"];
const FOOTER_SERVICES = ["llc-formation", "ein-registration", "business-funding", "business-credit", "credit-repair", "website-design", "business-consulting"];
const FOOTER_STATES = ["maryland", "virginia", "washington-dc", "florida", "texas", "new-york"];

export const Footer = () => {
  const { t } = useLang();
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const nameOf = (slug) => SERVICES_SEO.find((s) => s.slug === slug)?.name;
  const stName = (slug) => STATES.find((s) => s.slug === slug)?.name;

  return (
    <footer data-testid="footer" className="relative bg-[#080808] border-t border-white/10 pt-24 pb-10 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-2 lg:grid-cols-12 gap-10 pb-20"
        >
          <div className="col-span-2 lg:col-span-4">
            <Link to="/" className="font-display text-2xl text-white">{t.brand.name}<span className="text-gold-gradient"> {t.brand.suffix}</span></Link>
            <p className="mt-5 text-[#a3a3a3] max-w-sm leading-relaxed">{t.footer.tagline}</p>
            <button
              data-testid="footer-cta"
              onClick={() => go("booking")}
              className="group mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3.5 bg-[#d4af37] text-[#050505] font-medium hover:bg-[#e9d08a] transition-colors duration-300"
            >
              {t.footer.cta}
              <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </button>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#d4af37] mb-5">{t.footer.services}</div>
            <ul className="space-y-3">
              {FOOTER_SERVICES.map((slug) => (
                <li key={slug}>
                  <Link to={`/services/${slug}`} data-testid={`footer-svc-${slug}`} className="text-[#a3a3a3] hover:text-white transition-colors duration-300">{nameOf(slug)}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#d4af37] mb-5">{t.footer.locations}</div>
            <ul className="space-y-3">
              {FOOTER_STATES.map((slug) => (
                <li key={slug}>
                  <Link to={`/services/llc-formation/${slug}`} className="text-[#a3a3a3] hover:text-white transition-colors duration-300">LLC in {stName(slug)}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#d4af37] mb-5">{t.footer.resources}</div>
            <ul className="space-y-3">
              <li><Link to="/blog" data-testid="footer-blog" className="text-[#a3a3a3] hover:text-white transition-colors duration-300">{t.footer.blog}</Link></li>
              {LINK_IDS.slice(0, 4).map((id) => (
                <li key={id}><button data-testid={`footer-nav-${id}`} onClick={() => go(id)} className="text-[#a3a3a3] hover:text-white transition-colors duration-300">{t.nav[id]}</button></li>
              ))}
            </ul>
          </div>
        </motion.div>

        <div className="border-t border-white/10 pt-10">
          <div className="font-display text-[15vw] leading-none text-white/[0.04] select-none tracking-tighter">{t.brand.name}</div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-8 text-sm text-[#666]">
            <span>&copy; {new Date().getFullYear()} {t.brand.name} {t.brand.suffix}. {t.footer.rights}</span>
            <span>{t.footer.slogan}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
